/**
 * Cross-cohort comparison — Phase 8e. One row per federation joining the
 * rollup, vulnerability index, calibration, and reply quality so funders
 * can compare CCHI/MFOA, IFR/ECM, Sycamores/ECM, VyC/CHW in one table.
 * Internal CBO only.
 */

import React from 'react';
import { LayoutGrid, ExternalLink } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { useCrossCohort, type CrossCohortRow } from '@/hooks/ml';

// Backend returns numeric columns as strings (Decimal → JSON); coerce
// before any .toFixed() so a single bad value doesn't unmount the panel.
const num = (v: unknown): number | null => {
  if (v == null) return null;
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : null;
};
const fmt = (v: unknown): string => {
  const n = num(v);
  return n == null ? '—' : Math.round(n).toLocaleString();
};
const pct = (v: unknown): string => {
  const n = num(v);
  return n == null ? '—' : `${n.toFixed(1)}%`;
};
const ratio = (v: unknown, digits: number = 2): string => {
  const n = num(v);
  return n == null ? '—' : n.toFixed(digits);
};

const PVI_TONE = (pvi: number | null | undefined): string => {
  if (pvi == null) return 'bg-gray-100 text-gray-600';
  if (pvi >= 60) return 'bg-rose-100 text-rose-800';
  if (pvi >= 40) return 'bg-amber-100 text-amber-800';
  if (pvi >= 25) return 'bg-yellow-100 text-yellow-800';
  return 'bg-emerald-100 text-emerald-800';
};

export const CrossCohortComparisonCard: React.FC = () => {
  const q = useCrossCohort();

  if (q.isLoading) {
    return (
      <Card>
        <CardContent className="pt-4 text-sm text-gray-500">Loading cohorts…</CardContent>
      </Card>
    );
  }
  if (q.isError || !q.data?.cohorts.length) {
    return (
      <Card>
        <CardContent className="pt-4 text-sm text-gray-500">
          Cross-cohort comparison unavailable (internal CBO only).
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent className="pt-4 space-y-3">
        <div className="flex items-start gap-1.5 text-xs text-gray-600">
          <LayoutGrid className="h-3.5 w-3.5 mt-0.5 flex-shrink-0 text-indigo-500" />
          <span>
            Side-by-side cohort comparison. Each federation runs a distinct
            program (MFOA / ECM / CHW-Promotora) with its own calibration ratio;
            the projected-outcome cells use each federation's labeled ratio.
          </span>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-xs">
            <thead>
              <tr className="border-b border-gray-200 text-left text-gray-500">
                <th className="py-1.5 pr-3 font-medium">Federation</th>
                <th className="py-1.5 pr-3 font-medium text-right">CBOs</th>
                <th className="py-1.5 pr-3 font-medium text-right">Sends</th>
                <th className="py-1.5 pr-3 font-medium text-right">Reach</th>
                <th className="py-1.5 pr-3 font-medium text-right">Engagement (PVI-adj)</th>
                <th className="py-1.5 pr-3 font-medium text-center">PVI</th>
                <th className="py-1.5 pr-3 font-medium">Vulnerability mix</th>
                <th className="py-1.5 pr-3 font-medium text-right">Projected enrollments</th>
                <th className="py-1.5 pr-3 font-medium text-right">Projected nav</th>
                <th className="py-1.5 pr-3 font-medium text-center">Net sentiment</th>
              </tr>
            </thead>
            <tbody>
              {q.data.cohorts.map((c) => (
                <CohortRow key={c.federation_code} c={c} />
              ))}
            </tbody>
          </table>
        </div>

        <div className="text-[10px] text-gray-500 leading-relaxed">
          <strong>PVI</strong> = Population Vulnerability Index (weighted across
          HIGH=80 and MEDIUM=45 bands; LOW contributes 0, so the index reflects
          the harder-to-reach fraction only). <strong>Engagement (PVI-adj)</strong>
          = raw engagement × (1 + PVI/100), crediting cohorts reaching higher-LEP
          / older-adult / digitally-excluded populations. Projected outcomes use
          the active calibration ratio per federation; CCHI is client-validated
          (1.07x replies → enrollments, 0.93x clicks → nav), others are
          directional proxies pending client aggregates.
        </div>
      </CardContent>
    </Card>
  );
};

const CohortRow: React.FC<{ c: CrossCohortRow }> = ({ c }) => {
  return (
    <tr className="border-b border-gray-100 hover:bg-gray-50/40">
      <td className="py-2 pr-3">
        <div className="font-semibold text-gray-900">{c.federation_code}</div>
        <div className="text-[10px] text-gray-500">{c.program_code ?? '—'}</div>
      </td>
      <td className="py-2 pr-3 text-right tabular-nums">{fmt(c.cbo_count)}</td>
      <td className="py-2 pr-3 text-right tabular-nums">{fmt(c.sends)}</td>
      <td className="py-2 pr-3 text-right tabular-nums">{fmt(c.unique_members_engaged)}</td>
      <td className="py-2 pr-3 text-right tabular-nums">
        {(() => {
          const er = num(c.engagement_rate_pct);
          const pvi = num(c.population_vulnerability_index);
          if (er != null && pvi != null) {
            const adj = er * (1 + pvi / 100);
            const lift = 1 + pvi / 100;
            return (
              <>
                <div className="text-gray-900 font-medium">{pct(adj)}</div>
                <div className="text-[10px] text-violet-700">
                  {lift.toFixed(2)}× lift · PVI {pvi.toFixed(0)}
                </div>
              </>
            );
          }
          return (
            <>
              <div className="text-gray-900 font-medium">{pct(c.engagement_rate_pct)}</div>
              <div className="text-[10px] text-gray-500">
                {pct(c.reply_rate_pct)} reply · {pct(c.click_rate_pct)} click
              </div>
            </>
          );
        })()}
      </td>
      <td className="py-2 pr-3 text-center">
        <span
          className={`rounded px-2 py-0.5 font-semibold tabular-nums ${PVI_TONE(
            c.population_vulnerability_index,
          )}`}
        >
          {c.population_vulnerability_index != null
            ? Math.round(c.population_vulnerability_index)
            : '—'}
        </span>
      </td>
      <td className="py-2 pr-3 text-[11px] text-gray-600">
        <div>{pct(c.pct_non_english)} LEP</div>
        <div>{pct(c.pct_no_email)} no-email</div>
        <div>{pct(c.pct_age_cohort)} older adult</div>
      </td>
      <td className="py-2 pr-3 text-right tabular-nums">
        <div className="text-gray-900 font-medium">{fmt(c.projected_enrollments)}</div>
        {c.enrollment_outcome_label && (
          <div className="text-[10px] text-gray-500">
            {c.enrollment_outcome_label.replace(/_/g, ' ')}
            {num(c.enrollment_ratio) != null && ` · ${ratio(c.enrollment_ratio)}x`}
          </div>
        )}
      </td>
      <td className="py-2 pr-3 text-right tabular-nums">
        <div className="text-gray-900 font-medium">{fmt(c.projected_nav_supports)}</div>
        {c.nav_support_outcome_label && (
          <div className="text-[10px] text-gray-500">
            {c.nav_support_outcome_label.replace(/_/g, ' ')}
            {num(c.nav_support_ratio) != null && ` · ${ratio(c.nav_support_ratio)}x`}
          </div>
        )}
      </td>
      <td className="py-2 pr-3 text-center">
        {(() => {
          const sent = num(c.net_sentiment_score);
          return sent != null ? (
          <>
            <span
              className={`rounded px-2 py-0.5 font-semibold tabular-nums ${SENTIMENT_TONE(
                sent,
              )}`}
            >
              {sent > 0 ? '+' : ''}
              {sent.toFixed(2)}
            </span>
            <div className="text-[10px] text-gray-500 mt-0.5">
              {pct(c.sentiment_positive_pct)} pos · {pct(c.sentiment_negative_pct)} neg
            </div>
          </>
        ) : (
          <span className="text-gray-400">—</span>
        );
        })()}
      </td>
    </tr>
  );
};

const SENTIMENT_TONE = (s: number | null | undefined): string => {
  if (s == null) return 'bg-gray-100 text-gray-600';
  if (s >= 0.30) return 'bg-emerald-100 text-emerald-800';
  if (s >= 0.10) return 'bg-teal-100 text-teal-800';
  if (s >= -0.10) return 'bg-gray-100 text-gray-700';
  if (s >= -0.30) return 'bg-amber-100 text-amber-800';
  return 'bg-rose-100 text-rose-800';
};

export default CrossCohortComparisonCard;
